import React, { useContext, useEffect, useState } from 'react'
import { EPCRContext } from '../EPCR'
import BigModal from '../../../../components/Modals/BigModal'
//@MUI
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import AnDateTimePicker from '../../../../components/DateTime/DateTimePicker'
import moment from 'moment'
import ReportProblemIcon from '@mui/icons-material/ReportProblem'
import HelpCenterIcon from '@mui/icons-material/HelpCenter'
import Tooltip from '@mui/material/Tooltip'
import SimpleTextField from '../../../../components/Forms/SimpleTextField'
import VitalSignsPopup from './Popup'
import { DataGrid } from '@mui/x-data-grid'
import EPCRCard from '../../../../components/Cards/EPCRCard'
import NoRowsOverlay from '../../../../components/DataGrid/NoRowsOverlay'
import NextButton from '../NextButton'
import _news2calc from '../../_news2calc'
import _pewsCalc, {
  hasPewsRequiredFields,
  isUnder18,
  pewsTempWarning,
  resolvePewsAgeBand,
} from '../../_pewsCalc'

const Vitals = ({}) => {
  const { formValues, handleChange } = useContext(EPCRContext)
  const paediatric = isUnder18(formValues?.demographics)
  const [openObs, setOpenObs] = useState(false)
  const [existingSet, setExistingSet] = useState({})
  const [rows, setRows] = useState([])

  useEffect(() => {
    if (formValues?.vitals?.obs?.length > 0) {
      setRows(
        [...formValues.vitals.obs].sort(
          (a, b) => new Date(a.time) - new Date(b.time),
        ),
      )
    } else {
      console.log('no rows!')
    }
  }, [formValues])

  // Use effect to set time as now

  const AddNewButton = () => (
    <Button
      variant="contained"
      color="primary"
      size="small"
      onClick={(e) => setOpenObs(true)}
    >
      Add New
    </Button>
  )

  return (
    <Stack gap={2}>
      <EPCRCard>
        <Box display="flex" flexDirection="column" gap={2} padding={1} flex={1}>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="title2">Vital Signs</Typography>
            <AddNewButton />
          </Box>
          <Box sx={{ height: 400, width: '100%' }}>
            <DataGrid
              slots={{
                noRowsOverlay: () =>
                  NoRowsOverlay({
                    customButton: (
                      <Button
                        variant="outlined"
                        color="info"
                        fullWidth
                        onClick={(e) => setOpenObs(true)}
                      >
                        Add New
                      </Button>
                    ),
                  }),
              }}
              columns={[
                {
                  field: 'time',
                  headerName: 'Time',
                  // renderCell: (params) => moment(params.row.time).format('HH:mm'),
                  valueGetter: (value, row) => moment(row.time).format('HH:mm'),
                },
                {
                  field: 'NEWS2',
                  headerName: paediatric ? 'PEWS' : 'NEWS2',
                  align: 'center',
                  headerAlign: 'center',
                  renderCell: (params) => {
                    const obs = params.row
                    const warning = (title) => (
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        height="100%"
                      >
                        <Tooltip title={title}>
                          <HelpCenterIcon color="warning" />
                        </Tooltip>
                      </Box>
                    )
                    const scoreBox = (result, whiteText) => (
                      <Box
                        bgcolor={result.color}
                        width="100%"
                        height="100%"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        color={whiteText ? 'white' : 'inherit'}
                      >
                        {result.score}
                      </Box>
                    )
                    if (paediatric) {
                      const ageBand = resolvePewsAgeBand(
                        formValues?.demographics,
                        obs,
                      )
                      if (!ageBand) {
                        return warning(
                          "Enter the patient's date of birth, or select an estimated age group on this obs set, to calculate an age-appropriate PEWS score",
                        )
                      }
                      if (!hasPewsRequiredFields(obs)) {
                        return warning(
                          'You must complete all the PEWS observations (resp rate, resp distress, SpO2, oxygen, heart rate, BP or BP-not-recorded reason, and CRT) for a PEWS score to be calculated',
                        )
                      }
                      const pews = _pewsCalc({ obs, ageBand })
                      return scoreBox(
                        pews,
                        pews.score >= 9 || pews.score === 0,
                      )
                    }
                    if (
                      !obs?.respRate ||
                      !obs?.spo2 ||
                      !obs?.bpSys ||
                      !obs?.heartRate ||
                      !obs?.avpu ||
                      !obs?.temp
                    ) {
                      return warning(
                        'You must complete all relevant vital signs for a NEWS2 score to be calculated',
                      )
                    }
                    const news2 = _news2calc({
                      obs,
                      spo2Scale2: formValues?.pmhx?.spo2Scale2,
                    })
                    return scoreBox(
                      news2,
                      news2.score >= 7 || news2.score === 0,
                    )
                  },
                },

                {
                  field: 'gcs',
                  headerName: 'GCS',
                  align: 'center',
                  headerAlign: 'center',
                  renderCell: (params) => {
                    const e = params.row.gcsEyes
                    const v = params.row.gcsVerbal
                    const m = params.row.gcsMotor

                    if (!e && !v && !m) {
                      return null
                    } else if (!e || !v || !m) {
                      return (
                        <Box
                          width="100%"
                          height="100%"
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                        >
                          <ReportProblemIcon color="warning" />
                        </Box>
                      )
                    } else {
                      return (
                        <Box
                          width="100%"
                          height="100%"
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                        >
                          <Typography>
                            {Number(e) + Number(v) + Number(m)}
                          </Typography>
                        </Box>
                      )
                    }
                  },
                },
                {
                  field: 'respRate',
                  headerName: 'Resps',
                  align: 'center',
                  headerAlign: 'center',
                },
                {
                  field: 'heartRate',
                  headerName: 'Pulse',
                  align: 'center',
                  headerAlign: 'center',
                },
                {
                  field: 'spo2',
                  headerName: 'SPO2',
                  align: 'center',
                  headerAlign: 'center',
                },
                {
                  field: 'bp',
                  headerName: 'BP',
                  align: 'center',
                  headerAlign: 'center',
                  valueGetter: (value, row) =>
                    row.bpSys && row.bpDia
                      ? row.bpSys +
                        '/' +
                        row.bpDia +
                        (row.bpPosition ? ` (${row.bpPosition})` : '')
                      : row.bpDerogation || undefined,
                  minWidth: 120,
                  flex: 1,
                },
                {
                  field: 'temp',
                  headerName: 'Temp',
                  valueGetter: (value, row) => row?.temp && `${row.temp}°C`,
                  align: 'center',
                  headerAlign: 'center',
                  renderCell: (params) => {
                    const tempWarning =
                      paediatric && pewsTempWarning(params.row?.temp)
                    return (
                      <Box
                        width="100%"
                        height="100%"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        bgcolor={tempWarning || undefined}
                        color={tempWarning ? 'white' : 'inherit'}
                      >
                        {params.value}
                      </Box>
                    )
                  },
                },

                {
                  field: 'bgl',
                  headerName: 'BM',
                  valueGetter: (value, row) => row?.bgl && `${row.bgl}mmol/L`,
                  align: 'center',
                  headerAlign: 'center',
                },

                {
                  field: 'capRefill',
                  headerName: 'CRT',
                  valueGetter: (value, row) =>
                    row?.capRefill && `${row.capRefill}s`,
                  align: 'center',
                  headerAlign: 'center',
                },
                {
                  field: 'painScore',
                  headerName: 'Pain',
                  align: 'center',
                  headerAlign: 'center',
                },
                {
                  field: 'pefr',
                  headerName: 'PEFR',
                  align: 'center',
                  headerAlign: 'center',
                },
                {
                  field: 'etco2',
                  headerName: 'ETCO2',
                  align: 'center',
                  headerAlign: 'center',
                },

                {
                  field: 'avpu',
                  headerName: 'AVPU',
                  align: 'center',
                  headerAlign: 'center',
                },
                {
                  field: 'suppO2',
                  headerName: 'Oxygen',
                  align: 'center',
                  headerAlign: 'center',
                  valueGetter: (value, row) =>
                    row?.suppO2 === 'Yes' &&
                    (row?.suppO2Fio2 ||
                      (row?.respSupport && row?.respSupport !== 'None'))
                      ? `Yes (${[
                          row?.suppO2Fio2 && `${row.suppO2Fio2}%`,
                          row?.respSupport !== 'None' && row?.respSupport,
                        ]
                          .filter(Boolean)
                          .join(', ')})`
                      : row?.suppO2,
                },
                ...(paediatric
                  ? [
                      {
                        field: 'respDistress',
                        headerName: 'Distress',
                        align: 'center',
                        headerAlign: 'center',
                      },
                    ]
                  : []),
              ]}
              rows={rows}
              disableColumnMenu
              onRowClick={(params) => {
                setExistingSet(params.row)
                setOpenObs(true)
              }}
              //   getRowId={(row) => row.index}
              getRowId={(row) => moment(row.time).toISOString()}
            />
          </Box>
          <BigModal
            open={!!openObs || openObs === 0}
            onClose={() => {
              setOpenObs(false)
              setExistingSet({})
            }}
          >
            <VitalSignsPopup
              handleChange={handleChange}
              formValues={formValues}
              closePopup={() => {
                setOpenObs(false)
                setExistingSet({})
              }}
              existingSet={existingSet}
            />
          </BigModal>
        </Box>
      </EPCRCard>
      <NextButton newModule="Treatment" />
    </Stack>
  )
}

export default Vitals
